import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import IntersectGrid, { Backdrop } from "@/components/IntersectMotif";
import { FinalCTA } from "@/pages/Home";
import { IMG, PORTFOLIO_FILTERS } from "@/lib/content";
import { endpoints } from "@/lib/api";
import { useLang } from "@/lib/i18n";

export default function Portfolio() {
  const { t, isAr } = useLang();
  const [filter, setFilter] = useState("All");
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["portfolio", filter],
    queryFn: () => endpoints.portfolio(filter === "All" ? null : filter),
  });

  return (
    <>
      <PageHero eyebrow="Portfolio / Ventures" title="Building at the intersection." image={IMG.geometric}
        subtitle="Investments, ventures, partnerships and joint ventures developed by Intersect." />

      <section className="section-pad relative overflow-hidden">
        <Backdrop src={IMG.jeddahCorniche} opacity={0.1} />
        <div className="container-x relative z-10">
          <div className="flex flex-wrap gap-3 mb-14" data-testid="portfolio-filters">
            {PORTFOLIO_FILTERS.map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f.replace(/\s/g, "-").toLowerCase()}`}
                className={`px-5 py-2.5 text-xs tracking-wide uppercase border transition-colors duration-300 ${filter === f ? "border-primary-gold text-primary-gold" : "border-white/10 text-muted-text hover:border-primary-gold/50 hover:text-soft-stone"}`}>
                {t(f)}
              </button>
            ))}
          </div>

          {isLoading ? (
            <p className="text-muted-text">{t("Loading…")}</p>
          ) : items.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-primary-gold/10 border border-primary-gold/10">
              {items.map((c, i) => (
                <Link key={c.id} to={`/portfolio/${c.slug}`} className="card-lift bg-obsidian p-8 group hover:bg-charcoal transition-colors" data-testid={`portfolio-item-${i}`}>
                  <div className="flex items-center justify-between mb-8">
                    <span className="text-xs tracking-widest uppercase text-primary-gold">{t(c.relationship_type)}</span>
                    <span className="text-xs text-muted-text">{c.year}</span>
                  </div>
                  <h3 className="text-2xl font-light text-ivory group-hover:text-primary-gold transition-colors">{isAr && c.name_ar ? c.name_ar : c.name}</h3>
                  <p className="mt-3 text-sm text-muted-text">{c.sector} · {c.location}</p>
                  <p className="mt-4 text-sm leading-relaxed text-soft-stone line-clamp-3">{isAr && c.description_ar ? c.description_ar : c.description}</p>
                </Link>
              ))}
            </div>
          ) : (
            <Reveal>
              <div className="relative border border-primary-gold/15 p-12 md:p-20 text-center" data-testid="portfolio-empty">
                <IntersectGrid cols={3} rows={2} />
                <div className="relative z-10">
                  <h3 className="text-2xl md:text-4xl font-light text-ivory">{t("Our portfolio is taking shape.")}</h3>
                  <p className="mt-6 max-w-xl mx-auto text-base leading-relaxed text-muted-text">{t("Intersect is actively evaluating ventures, investment opportunities and strategic partnerships.")}</p>
                  <Link to="/opportunity" className="btn-gold mt-10" data-testid="portfolio-empty-cta">{t("Present an Opportunity")}</Link>
                </div>
              </div>
            </Reveal>
          )}
        </div>
      </section>
      <FinalCTA />
    </>
  );
}
